import SpaceDashboardOutlinedIcon from '@mui/icons-material/SpaceDashboardOutlined';
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';
import AssignmentIndOutlinedIcon from '@mui/icons-material/AssignmentIndOutlined';
import LocalPhoneOutlinedIcon from '@mui/icons-material/LocalPhoneOutlined';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import {
  BarChartIcon,
  ChatBubbleIcon,
  DashboardIcon,
  EnterIcon,
  EnvelopeClosedIcon,
  ExclamationTriangleIcon,
  FileTextIcon,
  Link2Icon,
  ReaderIcon,
  ShuffleIcon,
} from '@radix-ui/react-icons';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';
import PeopleOutlinedIcon from '@mui/icons-material/PeopleOutlined';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import BusinessCenterOutlinedIcon from '@mui/icons-material/BusinessCenterOutlined';
import HubOutlinedIcon from '@mui/icons-material/HubOutlined';
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';
import ManageAccountsOutlinedIcon from '@mui/icons-material/ManageAccountsOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import SupportAgentOutlinedIcon from '@mui/icons-material/SupportAgentOutlined';
import ContactPhoneOutlinedIcon from '@mui/icons-material/ContactPhoneOutlined';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import QuickreplyOutlinedIcon from '@mui/icons-material/QuickreplyOutlined';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import type { ComponentType, CSSProperties } from 'react';
import { featureFlags } from '@app/config/featureFlags';
import type { Permission } from '@shared/auth/permissions';
import type { ManagedChannelCode } from '@shared/types/domain';

export interface NavigationItem {
  key: string;
  path: string;
  icon: ComponentType<{ style?: CSSProperties; 'aria-hidden'?: boolean }>;
  permission?: Permission;
  additionalPermissions?: Permission[];
  sourcePermissions?: Array<{ code: ManagedChannelCode; permission: Permission }>;
}

export interface NavigationSection {
  key: string;
  items: NavigationItem[];
}

export const navigationSections: NavigationSection[] = [
  {
    key: 'nav.overview',
    items: [
      {
        key: 'nav.dashboard',
        path: '/dashboard',
        icon: DashboardIcon,
        permission: 'dashboard.view',
      },
      { key: 'notification.title', path: '/notifications', icon: NotificationsNoneOutlinedIcon },
    ],
  },
  {
    key: 'nav.communications',
    items: [
      {
        key: 'nav.tickets',
        path: '/tickets',
        icon: ReaderIcon,
        permission: 'tickets.view',
      },
      ...(featureFlags.phase2Available
        ? [
            {
              key: 'nav.inbox',
              path: '/inbox',
              icon: InboxOutlinedIcon,
              permission: 'inbox.view' as Permission,
            },
          ]
        : []),
      {
        key: 'nav.whatsapp',
        path: '/inbox/whatsapp',
        icon: WhatsAppIcon,
        permission: 'whatsapp.view',
      },
      {
        key: 'nav.liveChat',
        path: '/inbox/live-chat',
        icon: ChatBubbleIcon,
        permission: 'livechat.view',
      },
      ...(featureFlags.phase2Available
        ? [
            {
              key: 'nav.email',
              path: '/inbox/email',
              icon: EnvelopeClosedIcon,
              permission: 'inbox.view' as Permission,
            },
          ]
        : []),
      { key: 'nav.calls', path: '/calls', icon: LocalPhoneOutlinedIcon, permission: 'calls.view' },
      {
        key: 'nav.assignedToMe',
        path: '/assigned-to-me',
        icon: AssignmentIndOutlinedIcon,
        sourcePermissions: [
          { code: 'tickets', permission: 'tickets.view' },
          { code: 'whatsapp', permission: 'whatsapp.view' },
          { code: 'live_chat', permission: 'livechat.view' },
        ],
      },
    ],
  },
  {
    key: 'nav.operations',
    items: [
      {
        key: 'nav.knowledge',
        path: '/knowledge-base',
        icon: FileTextIcon,
        permission: 'knowledge.view',
      },
      {
        key: 'nav.escalations',
        path: '/escalations',
        icon: ExclamationTriangleIcon,
        permission: 'escalations.view',
      },
      {
        key: 'nav.reports',
        path: '/reports',
        icon: BarChartIcon,
        permission: 'reports.view',
      },
    ],
  },
  {
    key: 'nav.connectors',
    items: [
      {
        key: 'nav.kbMcp',
        path: '/connectors/kb-mcp',
        icon: Link2Icon,
        permission: 'knowledge.connectors.view',
      },
      {
        key: 'nav.restSource',
        path: '/connectors/rest-source',
        icon: EnterIcon,
        permission: 'knowledge.connectors.view',
      },
      {
        key: 'nav.kbDelivery',
        path: '/connectors/rest-delivery',
        icon: ShuffleIcon,
        permission: 'knowledge.connectors.view',
      },
    ],
  },
  {
    key: 'nav.customersFeedback',
    items: [
      {
        key: 'nav.liveChatFeedback',
        path: '/customers-feedback/live-chat',
        icon: ChatBubbleIcon,
        permission: 'feedback.livechat.view',
      },
      {
        key: 'nav.recoveryFeedback',
        path: '/customers-feedback/recovery-tracking',
        icon: FactCheckOutlinedIcon,
        permission: 'feedback.recovery.view',
      },
      {
        key: 'nav.eaCustomerSurvey',
        path: '/customers-feedback/ea-customer-survey',
        icon: AssignmentOutlinedIcon,
        permission: 'feedback.ea_survey.view',
      },
    ],
  },
  {
    key: 'nav.administration',
    items: [
      { key: 'nav.users', path: '/users', icon: PersonOutlinedIcon, permission: 'users.view' },
      {
        key: 'nav.roles',
        path: '/roles',
        icon: AdminPanelSettingsOutlinedIcon,
        permission: 'roles.view',
      },
      { key: 'nav.teams', path: '/teams', icon: GroupsOutlinedIcon, permission: 'teams.view' },
      {
        key: 'nav.entities',
        path: '/entities',
        icon: BusinessCenterOutlinedIcon,
        permission: 'entities.view',
      },
      {
        key: 'nav.channels',
        path: '/channels',
        icon: HubOutlinedIcon,
        permission: 'channels.view',
      },
      { key: 'nav.sla', path: '/sla', icon: TimerOutlinedIcon, permission: 'sla.view' },
      {
        key: 'notification.settings',
        path: '/notification-settings',
        icon: NotificationsActiveOutlinedIcon,
        permission: 'notifications.settings.view',
      },
      {
        key: 'nav.helixInbox',
        path: '/helix-inbox',
        icon: EnvelopeClosedIcon,
        permission: 'mailbox.inbox.view',
      },
      {
        key: 'nav.settings',
        path: '/settings',
        icon: SettingsOutlinedIcon,
        permission: 'settings.view',
      },
    ],
  },
  {
    key: 'nav.callsSettings',
    items: [
      {
        key: 'nav.cupolaCalls',
        path: '/calls/cupola',
        icon: ContactPhoneOutlinedIcon,
        permission: 'calls.settings.view',
      },
    ],
  },
  {
    key: 'nav.liveChatSettings',
    items: [
      {
        key: 'nav.liveChatMappings',
        path: '/live-chat/mappings',
        icon: ManageAccountsOutlinedIcon,
        permission: 'livechat.administrator',
      },
      {
        key: 'nav.liveChatDashboard',
        path: '/live-chat/dashboard',
        icon: SpaceDashboardOutlinedIcon,
        permission: 'reports.view',
        additionalPermissions: ['livechat.view'],
      },
    ],
  },
  {
    key: 'nav.whatsAppSettings',
    items: [
      {
        key: 'whatsAppAdmin.dashboard',
        path: '/whatsapp/dashboard',
        icon: SpaceDashboardOutlinedIcon,
        permission: 'whatsapp.settings.view',
      },
      {
        key: 'whatsAppAdmin.agents',
        path: '/whatsapp/agents',
        icon: SupportAgentOutlinedIcon,
        permission: 'whatsapp.settings.view',
      },
      {
        key: 'whatsAppAdmin.clients',
        path: '/whatsapp/clients',
        icon: PeopleOutlinedIcon,
        permission: 'whatsapp.settings.view',
      },
      {
        key: 'whatsAppAdmin.profiles',
        path: '/whatsapp/profiles',
        icon: ContactPhoneOutlinedIcon,
        permission: 'whatsapp.settings.view',
      },
      {
        key: 'whatsAppAdmin.actionReasons',
        path: '/whatsapp/action-reasons',
        icon: FactCheckOutlinedIcon,
        permission: 'whatsapp.settings.view',
      },
      {
        key: 'whatsAppAdmin.messageShortcuts',
        path: '/whatsapp/message-shortcuts',
        icon: QuickreplyOutlinedIcon,
        permission: 'whatsapp.settings.view',
      },
      {
        key: 'whatsAppAdmin.audit',
        path: '/whatsapp/audit',
        icon: HistoryOutlinedIcon,
        permission: 'whatsapp.audit.view',
      },
      {
        key: 'nav.whatsAppMappings',
        path: '/whatsapp/agent-mappings',
        icon: ManageAccountsOutlinedIcon,
        permission: 'whatsapp.administrator',
      },
    ],
  },
];
